import { Col, OverlayTrigger, Row, Tooltip } from "react-bootstrap";
import { BedSvg, CalendarIconSvg, MoonIconSvg, InfoIconSvg, UserIconSvg, ShipIconSvg } from "../../assets/svg";
import { Dispatch, SetStateAction, useContext, useState } from "react";
import { usePrivateContext } from "../../context/private.context";
import { approveRejectDiscountRequest } from "../../api/quotation.api";
import { splitDate } from "../../utils/splitDate";
import CustomModal from "../../components/ModalWrapper/ModalWrapper";
import CustomButton from "../../components/CustomButton/CustomButton";
import SnackbarContext from "../../context/snackbar.context";
import dayjs from "dayjs";

interface IDiscountTableRow {
  item: any;
  index: number;
  discountData: any;
  setDiscountData: Dispatch<SetStateAction<any>>;
}
interface IPayload {
  quotationId: string;
  approvalRemarks: string;
}

const DiscountTableRow:React.FC<IDiscountTableRow> = ({ item, index, discountData, setDiscountData }) => {
  const snackbarCtx = useContext(SnackbarContext);
  const { auth: { authData: { role } } } = usePrivateContext();
  const [open, setOpen] = useState<boolean>(false);
  const [payloadObj, setPayloadObj] = useState<IPayload>({quotationId: item?.quotationId, approvalRemarks:''});
  
  const handleRemarksChange = (e: any) => {
    const { value } = e.target;
    setPayloadObj((prevPayload: any) => ({ ...prevPayload, approvalRemarks: value }));
  }

  const handleBtnClick = async (status: string) => {
    const data = await approveRejectDiscountRequest({...payloadObj, status: status})
    if (data.status === 201) {
      const tempData = [...discountData];
      tempData[index].status = status;
      setDiscountData([...tempData]);
      setOpen(prev => !prev)
      snackbarCtx.toggleSnackbar({
        message: `Discount request ${status}!`,
        type: "SUCCESS",
        visible: true,
      });
    } else {
      snackbarCtx.toggleSnackbar({
        message: data?.response?.data?.message,
        type: "FAILURE",
        visible: true,
      });
    }
  }

  const formatNumberWithCommas = (num: number) => {
   return new Intl.NumberFormat('en-IN', { style: "currency", currency: "INR" }).format(num)
  }

  return (
    <div className="min-h-[171px] my-3 border rounded bg-light">
      <Row
        className="bg-[#F9FFF2] border-bottom rounded-top min-h-[34px] m-0 justify-between items-center "
        style={{ fontSize: "16px", fontWeight: "700" }}
      >
        <Col>
          <p className="m-0">Discount Amount : {formatNumberWithCommas(item?.discountAmount ?? 0)}</p>
        </Col>
        <Col className="flex gap-2 justify-end items-center">
          <p className="px-2 m-0 text-[#92278F]">{item?.quotation?.number}</p>
          <span
            className={`text-uppercase ${
              item?.status == "pending"
                ? "text-[#FFA500]"
                : item?.status == "approved"
                ? "text-[#17A300]"
                : "text-[#e3444a]"
            }`}
          >
            {item?.status}
          </span>
        </Col>
      </Row>
      <Row
        className="text-[#7A7A7A] mx-0 mt-3 justify-between"
        style={{ fontSize: "14px", lineHeight: "18.23px" }}
      >
        <Col xs={4} className="flex items-center">
          <p className="mx-1 my-auto"> {item?.quotation?.routeName}</p>
        </Col>
        <Col>
          <p className="m-0" style={{ fontWeight: "400" }}>
            Full Name
          </p>
          <p className="m-o" style={{ fontWeight: "700" }}>
            {item?.quotation?.customer?.firstName} {item?.quotation?.customer?.lastName}
          </p>
        </Col>
        <Col>
          <p className="m-0" style={{ fontWeight: "400" }}>
            Phone Number
          </p>
          <p className="m-o" style={{ fontWeight: "700" }}>
            {item?.quotation?.customer?.phoneNumber}
          </p>
        </Col>
        <Col>
          <p className="m-0" style={{ fontWeight: "400" }}>
            Email
          </p>
          <p className="m-o" style={{ fontWeight: "700" }}>
            {item?.quotation?.customer?.email}
          </p>
        </Col>
      </Row>
      <Row className="justify-between m-0 mt-2 text-[#7A7A7A]" style={{ fontSize: "17px", fontWeight: "500" }}>
        <Col xs={4} className="flex items-center">
          <CalendarIconSvg height={19} width={19} fill="#7a7a7a" />
          <p className="mx-1 my-auto">
            {" "}
            {dayjs(splitDate(item?.quotation?.itinerary?.startTime)).format("DD MMM YYYY")} -{" "}
            {dayjs(splitDate(item?.quotation?.itinerary?.endTime)).format("DD MMM YYYY")}
          </p>
        </Col>
        <Col className="flex items-center">
          <ShipIconSvg fill="#7a7a7a" height={20} width={18} />
          <span className="mx-1 my-auto">Cruise: {item?.quotation?.itinerary?.shipName || item?.quotation?.shipName || 'NA'}</span>
        </Col>
        <Col className="flex items-center">
          <MoonIconSvg fill="#7a7a7a" />
          <p className="mx-1 my-auto">{item?.quotation?.nights} Nights</p>
        </Col>
        <Col className="flex items-center">
          <UserIconSvg fill="#7a7a7a" />
          <p className="mx-1 my-auto">
            {Number(item?.quotation?.infants) + Number(item?.quotation?.children) + Number(item?.quotation?.adults)}{" "}
            Guests
          </p>
          <OverlayTrigger delay={{ hide: 0, show: 50 }} placement="bottom"
            overlay={
              <Tooltip>
                  {`Adult: ${item?.quotation?.adults || 0}`}
                  <br />
                  {`Children: ${item?.quotation?.children || 0}`}
                  <br />
                  {`Infant: ${item?.quotation?.infants || 0}`}
              </Tooltip>
            }>
            <span className="cursor-pointer">
              <InfoIconSvg fill="#D0566E" />
            </span>
          </OverlayTrigger>
        </Col>
        <Col className="flex items-center">
          <BedSvg fill="#7a7a7a" />
          <p className="mx-1 my-auto">
            {item?.quotation?.items?.length ?? 0} {item?.quotation?.items?.length > 1 ? "Cabins" : "Cabin"}
          </p>
          <OverlayTrigger delay={{ hide: 0, show: 50 }} placement="bottom"
            overlay={
              <Tooltip>
                  {item?.quotation?.items?.length && item?.quotation?.items?.map((cabin: any) => <>{cabin?.categoryName}<br/></>)}
              </Tooltip>
            }>
            <span className="cursor-pointer">
              <InfoIconSvg fill="#D0566E" />
            </span>
          </OverlayTrigger>
        </Col>
        {/* <Col className="flex items-center justify-content-end mx-2">
          <button className="border p-1 rounded bg-[#EEEEEE] text-nowrap my-auto text-[#3A3A3A]" style={{ fontSize: "14px", fontWeight: "400" }} onClick={()=>{setShow(true)}}>
            View Details
          </button>
        </Col> */}
      </Row>
      <Row
        className="text-[#7A7A7A] mx-0 mt-3 justify-content-between"
        style={{ fontSize: "14px", lineHeight: "18.23px" }}
      >
        <Col xs={4}>
          <p className="m-0" style={{ fontWeight: "400" }}>
            Request Remarks
          </p>
          <p className="m-o" style={{ fontWeight: "700" }}>
            {item?.requestRemarks}
          </p>
        </Col>
        <Col>
          <p className="m-0" style={{ fontWeight: "400" }}>
            Cabin Fare
          </p>
          <p className="m-o" style={{ fontWeight: "700" }}>
            {formatNumberWithCommas(item?.totalAmount ?? 0)}
          </p>
        </Col>
        {item?.status === "pending" && item?.quotation?.status === "ACTIVE" && role !== "Sales Agent" && (
          <Col className="flex justify-end mr-10">
            <CustomButton onButtonClick={() => setOpen((prev) => !prev)}>Take Action</CustomButton>
          </Col>
        )}
      </Row>
      <CustomModal show={open} handleClose={() => setOpen((prev) => !prev)}>
        <div className="min-w-[30vw] flex flex-col gap-4">
          <span>Discount Request</span>
          <div className="flex flex-col gap-3">
            <div className="flex justify-between text-base font-semi-bold text-muted">
              <span>Discount Amount :</span>
              <span>{formatNumberWithCommas(item?.discountAmount ?? 0)}</span>
            </div>
            <div className="flex justify-between text-base font-semi-bold text-muted">
              <span className="w-1/2">Request Remarks :</span>
              <span className="text-sm font-medium self-end italic w-fit">{item?.requestRemarks}</span>
            </div>
          </div>
          <div className="flex flex-col gap-2 mt-2 request-textarea-wrapper">
            <label htmlFor="remarks">Add Remarks</label>
            <textarea
              name="remarks"
              id="reamrks"
              className="w-100 min-h-[60px]"
              placeholder="Remarks"
              onChange={handleRemarksChange}
            ></textarea>
          </div>
          <div className="flex justify-center gap-4">
            <CustomButton
              fillColor={false}
              disabled={!payloadObj?.approvalRemarks}
              onButtonClick={() => handleBtnClick("rejected")}
            >
              Reject Request
            </CustomButton>
            <CustomButton disabled={!payloadObj?.approvalRemarks} onButtonClick={() => handleBtnClick("approved")}>
              Approve Request
            </CustomButton>
          </div>
        </div>
      </CustomModal>
    </div>
  );
}
export default DiscountTableRow